import { Meta, Title } from '@solidjs/meta';
import {
	ActivityIndicator,
	Container,
	Heading,
	Page,
	Grid,
	GridThird,
	GridTwoThirds,
	Dialog,
	DialogContent,
} from '@troon/ui';
import { createAsync } from '@solidjs/router';
import { createEffect, createSignal, createUniqueId, Show, Suspense } from 'solid-js';
import { clientOnly } from '@solidjs/start';
import dayjs, { timeFrameNameFromHours } from '@troon/dayjs';
import { produce } from 'solid-js/store';
import { coerceBoolean, createSearchStore } from '@troon/primitives/search-store';
import { useTrackEvent } from '@troon/analytics';
import { cachedGet } from '@troon/api-client';
import { TeeTimeFilters, TeeTimeFiltersFAB } from '../../components/tee-time-search-filters';
import { TeeTimeSearchHeader } from '../../components/tee-time-search-header';
import { NoFacilities } from '../../components/tee-time-search-no-facilities';
import { useUser } from '../../providers/user';
import { Revalidator } from '../../providers/visibility';
import { TeeTimeAlertForm } from '../../partials/tee-time-alerts';
import { TeeTimeAlertButton } from '../../components/tee-time-alert-button';
import { FacilityHeaderButtons } from '../../components/facility/header-buttons';
import { TeeTimeNoResultsAlert } from '../../components/tee-time-no-results-alert';
import { getSchema } from '../../components/tee-time-search';
import { UpsellAccessMinimal } from '../../components/upsells/access-minimal';
import type { Facility } from '@troon/api-client';
import type { ComponentProps } from 'solid-js';
import type { RouteDefinition } from '@solidjs/router';
import type { z } from 'zod';

const TeeTimeResults = clientOnly(async () => ({
	default: (await import('@troon/tee-times/results')).TeeTimeResults,
}));

export default function TeeTimeSearchPage() {
	const user = useUser();
	const trackEvent = useTrackEvent();

	const [teeTimeAlertRange, setTeeTimeAlertRange] = createSignal<null | [number, number]>(null);
	const teeTimeAlertDialogId = createUniqueId();
	createEffect(() => {
		// Manually triggered dialogs that don't use DialogTrigger must call dialogOpen
		if (teeTimeAlertRange()) {
			trackEvent('dialogOpened', { key: 'tee-time-alert-inline' });
		}
	});

	const schema = getSchema();

	const [filters, setFilters] = createSearchStore(
		user()?.troonAccessProductType
			? schema.extend({
					access: coerceBoolean().nullish().default(true),
				})
			: schema,
	);

	const facilities = createAsync(
		async () => {
			const query = {
				favorite: filters.favorites || undefined,
				location: filters.lat && filters.lon ? { latitude: filters.lat, longitude: filters.lon, radiusMiles: 50 } : {},
				idOrSlugs: filters.facilities || undefined,
				regionIds: filters.regionId || undefined,
				supportsTroonAccess: filters.premiumRates || undefined,
				supportsAccessPoints: filters.accessPoints || undefined,
				isBookable: true,
			} satisfies Parameters<typeof getFacilities>[0]['query'];

			if (
				!query.regionIds?.length &&
				!query.idOrSlugs?.length &&
				(!query.location?.latitude || !query.location?.longitude)
			) {
				return null;
			}
			const res = await getFacilities({ query });
			return res?.facilities ?? [];
		},
		{ deferStream: true },
	);

	return (
		<Revalidator keys={['/v1/tee-times']}>
			<div class="-mb-6 grow bg-neutral-100 md:-mb-12">
				<Title>Book Tee Times | Access Exclusive Rates & Earn Rewards | Golf with Access</Title>
				<Meta
					name="description"
					content="Search and book tee times at 200+ top courses. Access members save up to 50% and earn points toward free rounds every time they play."
				/>
				<Heading as="h1" class="sr-only">
					Search tee times with Troon
				</Heading>

				<TeeTimeSearchHeader
					class="border-b border-neutral"
					trackQuery
					onSearchUpdate={() => {
						setFilters(
							produce((s) => {
								s.filterFacilities = null;
							}),
						);
					}}
					filters={filters}
					setFilters={setFilters}
					scrollHeader={
						<>
							<p class="text-xl font-semibold">{filters.query}</p>
							<p class="text-sm text-neutral-800">
								{dayjs(filters.date).format('ddd, MMM D')} • {filters.players} player{filters.players > 1 ? 's' : ''} •{' '}
								{timeFrameNameFromHours(filters.startAt, filters.endAt, true)}
							</p>
						</>
					}
				/>

				<Container>
					<Page class="pt-0 lg:pt-8">
						<Grid>
							<GridThird>
								<div
									// Manual top calculations are depend on the height of the TeeTimeSearchFields section
									class="sticky top-54 hidden max-h-[calc(100dvh-15rem)] flex-col gap-6 overflow-auto rounded-lg border border-neutral bg-white p-6 lg:flex xl:top-34 xl:max-h-[calc(100dvh-11rem)]"
								>
									<Heading as="h2" size="h5">
										Filter by
									</Heading>

									<TeeTimeFilters
										userHasFavorites={!!facilities.latest?.reduce((memo, f) => (memo += f.isFavorite ? 1 : 0), 0)}
										facilities={
											facilities.latest?.map((f) => ({
												...f,
												logo: f.logo ?? null,
												address: f.address ?? null,
											})) ?? []
										}
										filters={filters}
										setFilters={setFilters}
										withFacilityFilters
										withAccessPlusOffers
										withGolfnowOffers={facilities.latest?.some((f) => f.supportsHotDeals)}
									/>
								</div>
							</GridThird>

							<GridTwoThirds>
								<Suspense
									fallback={
										<div class="min-h-dvh">
											<ActivityIndicator />
										</div>
									}
								>
									<Show when={facilities.latest} fallback={<NoFacilities filters={filters} setFilters={setFilters} />}>
										{(facilities) => (
											<>
												<TeeTimeResults
													facilities={facilities() as Array<unknown> as Array<z.infer<typeof Facility>>}
													filters={filters}
													setFilters={setFilters}
													teeTimeHref={(teeTime, params) =>
														`/course/${teeTime.facility.slug}/reserve-tee-time/${teeTime.id}?${params.toString()}`
													}
													includeFeaturedOffers
													includeInlineOffers
													includeUpsellBanner
													upsellBanner={<UpsellAccessMinimal location="multi-course tee time search" />}
													userAccessProductType={
														user()?.troonAccessProductType as ComponentProps<
															typeof TeeTimeResults
														>['userAccessProductType']
													}
													alertRenderer={(item) => (
														<TeeTimeAlertButton
															aria-haspopup="dialog"
															aria-controls={teeTimeAlertRange() ? teeTimeAlertDialogId : undefined}
															aria-expanded={!!teeTimeAlertRange()}
															onClick={() => setTeeTimeAlertRange([item().startAt, item().endAt])}
														>
															{item().content}
														</TeeTimeAlertButton>
													)}
													facilityHeaderContent={(slug: string) => (
														<Show when={facilities().find((f) => f.slug === slug)}>
															{(facility) => <FacilityHeaderButtons facility={facility()} useFacilityDialog />}
														</Show>
													)}
													noResultsContent={() => <TeeTimeNoResultsAlert facilities={facilities()} filters={filters} />}
													noFacilitiesContent={() => (
														<NoFacilities noResults filters={filters} setFilters={setFilters} />
													)}
												/>
												<Dialog
													id={teeTimeAlertDialogId}
													key="tee-time-alert-inline"
													open={!!teeTimeAlertRange()}
													onOpenChange={(open) => {
														if (!open) {
															setTeeTimeAlertRange(null);
														}
													}}
												>
													<DialogContent header="Create a Tee Time Alert" headerLevel="h3" height="fit">
														<TeeTimeAlertForm
															facilities={facilities()}
															selectedDay={filters.date}
															selectedTime={teeTimeAlertRange()!}
															selectedPlayers={filters.players}
															courseSlugs={facilities().flatMap((f) => f.courses.map((c) => c.slug))}
															onCancel={() => setTeeTimeAlertRange(null)}
															onSaved={() => setTeeTimeAlertRange(null)}
														/>
													</DialogContent>
												</Dialog>
											</>
										)}
									</Show>
								</Suspense>
							</GridTwoThirds>
						</Grid>

						<div class="lg:hidden">
							<TeeTimeFiltersFAB
								userHasFavorites={!!facilities.latest?.reduce((memo, f) => (memo += f.isFavorite ? 1 : 0), 0)}
								facilities={facilities.latest ?? []}
								filters={filters}
								setFilters={setFilters}
								withFacilityFilters
								withAccessPlusOffers
								withGolfnowOffers={facilities.latest?.some((f) => f.supportsHotDeals)}
							/>
						</div>
					</Page>
				</Container>
			</div>
		</Revalidator>
	);
}

const getFacilities = cachedGet('/v0/facilities');

export const route = { info: { nav: { sticky: false } } } satisfies RouteDefinition;
